import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  forwardRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { MatIcon } from '@angular/material/icon';
import { AvatarComponent } from '../avatar/avatar.component';
import { KiKontoPillComponent } from '../ki-konto-pill/ki-konto-pill.component';
import { AccountKind } from '../../models/customer.model';

export type PersonSelectVariant = 'default' | 'bundle';

export interface PersonOption {
  id: string;
  displayName: string;
  kind?: AccountKind;
}

interface KeyboardItem {
  id: string;
  isNone: boolean;
  displayName: string;
  kind?: AccountKind;
}

@Component({
  selector: 'app-person-select',
  standalone: true,
  imports: [TranslatePipe, MatIcon, AvatarComponent, KiKontoPillComponent, CdkOverlayOrigin, CdkConnectedOverlay],
  templateUrl: './person-select.component.html',
  styleUrl: './person-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PersonSelectComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PersonSelectComponent),
      multi: true,
    },
  ],
})
export class PersonSelectComponent implements ControlValueAccessor, Validator {
  readonly label = input<string>('');
  readonly placeholderKey = input<string>('personSelect.placeholder');
  readonly searchPlaceholderKey = input<string>('personSelect.searchPlaceholder');
  readonly emptyKey = input<string>('personSelect.empty');
  readonly noneKey = input<string>('personSelect.none');
  readonly required = input<boolean>(false);
  readonly allowNone = input<boolean>(true);
  readonly options = input<PersonOption[]>([]);
  readonly variant = input<PersonSelectVariant>('default');

  readonly open = signal(false);
  readonly query = signal('');
  readonly disabled = signal(false);
  readonly activeIndex = signal(-1);

  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('triggerBtn');
  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  private readonly internalValue = signal<string>('');
  readonly value = this.internalValue.asReadonly();

  readonly selected = computed(() => {
    const id = this.internalValue();
    if (!id) return null;
    return this.options().find((o) => o.id === id) ?? null;
  });

  readonly filtered = computed(() => {
    const q = this.query().trim().toLowerCase();
    if (!q) return this.options();
    return this.options().filter((o) => o.displayName.toLowerCase().includes(q));
  });

  readonly keyboardItems = computed<KeyboardItem[]>(() => {
    const items: KeyboardItem[] = this.filtered().map((o) => ({
      id: o.id,
      isNone: false,
      displayName: o.displayName,
      kind: o.kind,
    }));
    if (this.allowNone()) {
      items.unshift({ id: '', isNone: true, displayName: '' });
    }
    return items;
  });

  readonly activeId = computed<string | null>(() => {
    const idx = this.activeIndex();
    const items = this.keyboardItems();
    if (idx < 0 || idx >= items.length) return null;
    return this.optionDomId(items[idx]);
  });

  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;

  constructor() {
    effect(() => {
      if (this.open()) {
        queueMicrotask(() => this.searchInput()?.nativeElement.focus());
      }
    });
    effect(() => {
      this.required();
      this.onValidatorChange();
    });
  }

  writeValue(value: string | null | undefined): void {
    this.internalValue.set(value ?? '');
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    if (isDisabled) this.open.set(false);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.required()) return null;
    const value = (control.value ?? '') as string;
    return value.trim() ? null : { required: true };
  }

  toggle(): void {
    if (this.disabled()) return;
    if (this.open()) {
      this.close();
      return;
    }
    this.query.set('');
    this.activeIndex.set(this.initialActiveIndex());
    this.open.set(true);
  }

  close(returnFocus = true): void {
    if (!this.open()) return;
    this.open.set(false);
    this.activeIndex.set(-1);
    this.onTouched();
    if (returnFocus) {
      queueMicrotask(() => this.trigger()?.nativeElement.focus());
    }
  }

  selectByItem(item: KeyboardItem): void {
    if (item.isNone) {
      this.clearSelection();
      return;
    }
    this.selectOption(item.id);
  }

  selectOption(id: string): void {
    this.internalValue.set(id);
    this.onChange(id || null);
    this.close();
  }

  clearSelection(): void {
    this.internalValue.set('');
    this.onChange(null);
    this.close();
  }

  onSearchInput(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
    this.activeIndex.set(this.initialActiveIndex());
  }

  onSearchKeydown(event: KeyboardEvent): void {
    const items = this.keyboardItems();
    if (items.length === 0) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex.update((i) => (i + 1) % items.length);
        return;
      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex.update((i) => (i - 1 + items.length) % items.length);
        return;
      case 'Home':
        event.preventDefault();
        this.activeIndex.set(0);
        return;
      case 'End':
        event.preventDefault();
        this.activeIndex.set(items.length - 1);
        return;
      case 'Enter': {
        event.preventDefault();
        const idx = this.activeIndex();
        if (idx >= 0 && idx < items.length) this.selectByItem(items[idx]);
        return;
      }
      case 'Escape':
        event.preventDefault();
        this.close();
        return;
    }
  }

  optionDomId(item: KeyboardItem): string {
    return `ps-opt-${item.isNone ? 'none' : item.id}`;
  }

  isActive(item: KeyboardItem): boolean {
    return this.activeId() === this.optionDomId(item);
  }

  isSelected(item: KeyboardItem): boolean {
    if (item.isNone) return !this.selected();
    return this.selected()?.id === item.id;
  }

  private initialActiveIndex(): number {
    const items = this.keyboardItems();
    const selectedId = this.internalValue();
    const idx = items.findIndex((it) => (selectedId ? it.id === selectedId : it.isNone));
    return idx >= 0 ? idx : items.length > 0 ? 0 : -1;
  }
}
